import { z } from "zod";
const ordinal = (count) => Array.from({ length: count }, (_, i) => `${i + 1}.º`);
/** Every workshop is offered at every level, in this order; a level may describe one in its own words. */
const WORKSHOPS = [
    { id: "barro", name: "Modelado en barro", description: "Modelan una pieza sencilla: motricidad fina, creatividad y técnicas tradicionales." },
    { id: "amaranto", name: "Dulce de amaranto", description: "Preparan una alegría de amaranto y la relacionan con la comunidad y las tradiciones." },
    { id: "cacao", name: "Molienda de cacao", description: "Molienda tradicional en piedra: cómo una materia prima se transforma en alimento." },
    { id: "pintura", name: "Pintura con colores naturales", description: "Pigmentos como la grana cochinilla: color, tono y aplicación." },
    { id: "collares", name: "Elaboración de collares", description: "Una pieza personal: secuencia, composición y expresión individual." },
    /** New workshop (October 2026): counting and the calendar of the days as in ancient Mexico. */
    { id: "numeros", name: "Números prehispánicos", description: "Aprenden a contar como en el México antiguo: la cuenta de veinte con puntos y símbolos, y el calendario de los días." },
    { id: "marketing", name: "Plática: marketing digital para turismo", description: "Redes sociales, sitios web y herramientas digitales con ejemplos reales de turismo." },
];
const workshops = (descriptions = {}) => WORKSHOPS.map((workshop) => ({ ...workshop, description: descriptions[workshop.id] ?? workshop.description }));
const olderStudents = {
    cacao: "Chocolate artesanal con la molienda del cacao en molcajete de piedra.",
    amaranto: "Preparación de la alegría, dulce tradicional a base de amaranto.",
    pintura: "Pigmentos como grana cochinilla y cempasúchil y sus usos culturales y artísticos.",
};
export const SCHOOL_LEVELS = [
    {
        id: "primaria",
        name: "Primaria",
        grades: ordinal(6),
        gradeUnit: "grado",
        includedWorkshops: 2,
        includes: "2 talleres didácticos, danza prehispánica, show de aves, degustación regional, alimentos y recorrido por la Zona Arqueológica y el Museo de Sitio con guía certificado.",
        workshops: workshops(),
    },
    {
        id: "secundaria",
        name: "Secundaria",
        grades: ordinal(3),
        gradeUnit: "grado",
        includedWorkshops: 2,
        includes: "2 talleres a elegir, actividades culturales, degustación regional, alimentos y visita guiada a la Zona Arqueológica.",
        workshops: workshops({
            cacao: "Conocen el proceso tradicional y elaboran chocolate artesanal.",
            amaranto: "Descubren un alimento tradicional y participan en su elaboración.",
            pintura: "Exploran pigmentos como la grana cochinilla y su valor cultural.",
            collares: "Desarrollan coordinación, creatividad y trabajo manual.",
        }),
    },
    {
        id: "preparatoria",
        name: "Preparatoria",
        grades: ordinal(6),
        gradeUnit: "semestre",
        includedWorkshops: 3,
        includes: "3 talleres didácticos, plática de marketing digital para turismo, danza prehispánica, visita guiada, degustación regional y alimentos.",
        workshops: workshops(olderStudents),
    },
    {
        id: "universidad",
        name: "Universidad",
        grades: ordinal(10),
        gradeUnit: "semestre",
        includedWorkshops: 3,
        includes: "3 talleres didácticos, plática de marketing digital para turismo, danza prehispánica, visita guiada, degustación regional y alimentos.",
        workshops: workshops(olderStudents),
    },
];
export const schoolLevel = (id) => SCHOOL_LEVELS.find((level) => level.id === id);
/** What a school quote records: levels, and per level the grades and workshops chosen. */
export const schoolDetailsSchema = z
    .object({
    levels: z.array(z.enum(["primaria", "secundaria", "preparatoria", "universidad"])).max(4),
    grades: z.record(z.string().max(20), z.array(z.string().max(8)).max(12)).default({}),
    workshops: z.record(z.string().max(20), z.array(z.string().max(20)).max(8)).default({}),
    /** Teachers who come with the group; all of them are courtesy (free). */
    teachers: z.number().int().min(0).max(500).optional(),
    /** Other adults with the group (parents, chaperones); a count for the visit, not a price. */
    companions: z.number().int().min(0).max(500).optional(),
    /** Menu the group chose (one of SCHOOL_VISIT_MENUS); empty until they decide. */
    menu: z.string().trim().max(60).optional(),
})
    .strict();
/** "Primaria 4.º, 5.º y 6.º grado · Talleres: Cacao y Collares" per level, for documents. */
export function describeSchoolDetails(details) {
    return (details?.levels ?? []).flatMap((id) => {
        const level = schoolLevel(id);
        if (!level)
            return [];
        const grades = (details?.grades[id] ?? []).filter((g) => level.grades.includes(g));
        const workshops = (details?.workshops[id] ?? [])
            .map((w) => level.workshops.find((item) => item.id === w))
            .filter((w) => Boolean(w));
        return [{ level, grades, workshops }];
    });
}
export function listJoin(items) {
    return items.length < 2 ? (items[0] ?? "") : `${items.slice(0, -1).join(", ")} y ${items[items.length - 1]}`;
}
/** What every school visit includes, with the brochure illustration for each item. */
export const SCHOOL_VISIT_INCLUDES = [
    { icon: "hands", title: "Talleres didácticos", text: "Elegidos según el grado y el proyecto escolar." },
    { icon: "sun", title: "Danza prehispánica", text: "Ceremonia de agradecimiento a la Madre Tierra." },
    { icon: "bird", title: "Show de aves", text: "Parte de las actividades culturales de la jornada." },
    { icon: "grain", title: "Degustación regional", text: "Mermeladas y salsas de xoconostle, nopal y tuna." },
    { icon: "plate", title: "Alimentos completos", text: "Menú elegido por el grupo, agua de sabor y fruta." },
    { icon: "pyramid", title: "Zona Arqueológica", text: "Recorrido con guía certificado y visita al Museo de Sitio." },
];
/** Reference itinerary from the brochures; it is adjusted with each group. */
export const SCHOOL_VISIT_ITINERARY = [
    ["9:00 - 9:45", "Danza prehispánica de bienvenida"],
    ["9:45 - 13:00", "Talleres, show de aves y danza"],
    ["13:00 - 13:30", "Comida"],
    ["13:30 - 16:00", "Zona Arqueológica y Museo de Sitio"],
    ["16:30", "Reunión y salida"],
];
export const SCHOOL_VISIT_MENUS = {
    primaria: ["Taquitos dorados", "Mixiote a la cacerola", "Tacos de guisado", "Mole con pollo"],
    secundaria: ["Mixiote a la cacerola", "Taquitos dorados"],
    preparatoria: ["Mixiote a la cacerola", "Taquitos dorados"],
    universidad: ["Mixiote a la cacerola", "Taquitos dorados"],
};
export const SCHOOL_VISIT_NOTES = [
    "Todos los profesores que acompañan al grupo son cortesía (sin costo).",
    "Menores de 3 años: solo pagan menú si lo requieren ($70 MXN).",
    "Ruta Mágica no ofrece transporte ni cotizaciones de transporte para visitas escolares.",
];
export const SCHOOL_VISIT_CONDITIONS = [
    ["Credencial vigente", "Estudiantes y docentes presentan credencial vigente para acreditar la exención de acceso del INAH cuando corresponda."],
    ["Otros beneficios de acceso", "Pensionados, jubilados y personas adultas mayores acreditan los requisitos vigentes del INAH."],
    ["Familias y acompañantes", "Cubren su boleto de acceso a la Zona Arqueológica cuando corresponda."],
    ["Visita guiada", "El recorrido por la Zona Arqueológica está a cargo de guías certificados."],
    ["Organización del grupo", "Los talleres se adaptan al grado y al proyecto escolar; las condiciones están sujetas a disponibilidad y disposiciones vigentes."],
];
/** Public contacts printed on the brochures. */
export const RUTA_MAGICA_CONTACTS = [
    { icon: "chat", label: "WhatsApp", value: "55 9147 2788" },
    { icon: "mail", label: "Correo", value: "rutamagica@yahoo.com.mx" },
    { icon: "mail", label: "Correo", value: "teotihuacanrutamagica@gmail.com" },
];
/** Menu options for the chosen levels (all levels when none is chosen). */
export function schoolMenus(levels) {
    const ids = (levels.length ? levels : SCHOOL_LEVELS.map((l) => l.id));
    return [...new Set(ids.flatMap((id) => SCHOOL_VISIT_MENUS[id] ?? []))];
}
